import { useState, useEffect, useRef } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import "@fontsource/merienda";
import "@fontsource/manrope";

import dishImage from "../assets/images/pngs/profile.png";

const testimonials = [
  {
    image: dishImage,
    name: "Priya Verma",
    role: "Brampton, ON, Canada",
    text: "From order tracking to payment collection it’s all automated. I finally have time to focus on cooking and growing my business.",
  },
  {
    image: dishImage,
    name: "Neha Sinha",
    role: "Jersey City, NJ, USA",
    text: "Managing 100+ customers daily was overwhelming. With this system, I finally feel in control and my team works faster too.",
  },
  {
    image: dishImage,
    name: "Sanjay Kulkarni",
    role: "Vancouver, BC, Canada",
    text: "Before this, handling payments was a mess. Now I get reminders, automated bills, and even reports at month-end super convenient.",
  },
  {
    image: dishImage,
    name: "Anu Singh",
    role: "Toronto",
    text: "This tool completely changed how I manage subscriptions and deliveries.",
  },
  {
    image: dishImage,
    name: "Rahul Mehta",
    role: "Cambridge",
    text: "My monthly revenue tracking is now crystal clear.",
  },
];

export default function TestimonialCarousel() {
  const [current, setCurrent] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const sliderRef = useRef(null);

  // Responsive
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setCardsPerView(3);
      else if (window.innerWidth >= 768) setCardsPerView(2);
      else setCardsPerView(1);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    if (current > totalSlides - 1) {
      setCurrent(0);
    }
  }, [cardsPerView]);

  const totalSlides = Math.max(testimonials.length - cardsPerView + 1, 1);

  // Auto slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev >= totalSlides - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [totalSlides]);

  // Touch handlers
  const handleStart = (e) => {
    setIsDragging(true);
    startX.current = e.type === "mousedown" ? e.pageX : e.touches[0].clientX;
  };

  const handleEnd = (e) => {
    if (!isDragging) return;
    setIsDragging(false);

    const endX = e.type === "mouseup" ? e.pageX : e.changedTouches[0].clientX;

    const diff = startX.current - endX;

    if (diff > 50 && current < totalSlides - 1) {
      setCurrent(current + 1);
    } else if (diff < -50 && current > 0) {
      setCurrent(current - 1);
    }
  };

  const next = () =>
    setCurrent((prev) => (prev >= totalSlides - 1 ? 0 : prev + 1));

  const prev = () =>
    setCurrent((prev) => (prev <= 0 ? totalSlides - 1 : prev - 1));

  return (
    <div className="relative  bg-gradient-to-b from-orange-50 to-white overflow-hidden">
      <div>
        {/* LEFT FADE */}
        <div className="absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-white to-transparent z-10 hidden md:block" />

        {/* RIGHT FADE */}
        <div className="absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-white to-transparent z-10 hidden md:block" />

        {/* SLIDER */}
        <div className="overflow-hidden">
          <div
            ref={sliderRef}
            className="flex transition-transform duration-500 ease-out"
            style={{
              transform: `translateX(-${current * (100 / cardsPerView)}%)`,
            }}
            onMouseDown={handleStart}
            onMouseUp={handleEnd}
            onTouchStart={handleStart}
            onTouchEnd={handleEnd}
          >
            {testimonials.map((item, index) => (
              <div
                key={index}
                className="flex-shrink-0 px-3"
                style={{ width: `${100 / cardsPerView}%` }}
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg     border border-orange-100 h-full flex flex-col justify-between">
                  <div>
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-5 h-5 fill-yellow-400 text-yellow-400"
                        />
                      ))}
                    </div>

                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.text}
                    </p>
                  </div>

                  <div className="flex items-center gap-4 mt-8">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-12 w-12 rounded-full border-2 border-orange-200"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {item.name}
                      </h3>
                      <span className="text-sm text-gray-500">{item.role}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DOTS */}
      <div className="flex justify-center mt-10 gap-4">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrent(index)}
            className="relative h-5 w-5 flex items-center justify-center"
          >
            {/* Outer Circle */}
            <span
              className={`absolute h-5 w-5 rounded-full border-2 transition-all duration-300
        ${current === index ? "border-orange-500" : "border-orange-300"}`}
            />

            {/* Inner Dot */}
            {current === index && (
              <span className="h-2.5 w-2.5 bg-orange-500 rounded-full transition-all duration-300" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
